import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SheetShell } from './SheetShell';

/**
 * ConfirmDialog — the ratified replacement for window.confirm (corpus P7; design 01-architecture §4.2).
 *
 * Why the native confirm was a defect and not a polish item (01-Company D-C8): it is unstyled and
 * outside the design system; on a phone it renders as an OS alert, not the ratified bottom sheet; and
 * its BUTTONS render in the browser's language while the message goes through t() — one dialog, two
 * languages, the class that got a release rejected on sight. Here message AND buttons go through t().
 *
 * Contract:
 *  - `message` arrives already interpolated by the caller (real counts, never a category — X-5).
 *  - `destructive` renders the confirm button rose. The affordance follows the reversibility.
 *  - `blocked` renders NO confirm button at all — an explanation with a destructive button says
 *    "this can happen if you insist", and someone will insist. Optional `offerLabel`/`onOffer`
 *    carries a non-destructive way out (an offer, not a dead end).
 *
 * Shape: bottom sheet under 768px, centered card at 768px+. This component reads the viewport
 * DIRECTLY, deliberately breaking ValueSheet's caller-passes-shape rule: that rule exists because a
 * pane-embedded editor must follow its CONTAINER, which the viewport cannot stand in for — but this
 * dialog is a fixed full-viewport overlay that is never embedded in a pane, so the viewport IS its
 * container. Threading isMobile through six call sites would buy no correctness here.
 */
export interface ConfirmDialogProps {
  message: string;
  title?: string;
  confirmLabel?: string;   // defaults to t('confirmDialog.delete') when destructive, t('confirmDialog.confirm') otherwise
  cancelLabel?: string;    // defaults to t('confirmDialog.cancel')
  destructive?: boolean;
  blocked?: boolean;
  offerLabel?: string;     // blocked mode only — the non-destructive way out
  onOffer?: () => void;
  onConfirm?: () => void;  // required unless blocked
  onClose: () => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({ message, title, confirmLabel, cancelLabel, destructive, blocked, offerLabel, onOffer, onConfirm, onClose }) => {
  const { t } = useTranslation('common');
  const [isPhone, setIsPhone] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsPhone(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <SheetShell shape={isPhone ? 'sheet' : 'center'} onClose={onClose} testId="confirm-dialog">
      <div className="p-4 flex flex-col gap-4">
        {title && <div className="text-[11px] font-semibold text-text-muted uppercase tracking-wide">{title}</div>}
        <p data-testid="confirm-dialog-message" className="text-sm text-text-strong">{message}</p>
        <div className="flex items-center justify-end gap-2 flex-wrap">
          <button type="button" autoFocus data-testid="confirm-dialog-cancel" onClick={onClose}
            className="min-h-[44px] px-4 rounded-lg border border-border-neutral text-sm font-semibold text-text-strong hover:bg-canvas">
            {cancelLabel ?? t('confirmDialog.cancel')}
          </button>
          {blocked
            ? (offerLabel && onOffer && (
              <button type="button" data-testid="confirm-dialog-offer" onClick={onOffer}
                className="min-h-[44px] px-4 rounded-lg bg-primary text-white text-sm font-semibold">
                {offerLabel}
              </button>
            ))
            : (
              <button type="button" data-testid="confirm-dialog-confirm" onClick={onConfirm}
                className={`min-h-[44px] px-4 rounded-lg text-sm font-semibold text-white ${destructive ? 'bg-opt-rose' : 'bg-primary'}`}>
                {confirmLabel ?? (destructive ? t('confirmDialog.delete') : t('confirmDialog.confirm'))}
              </button>
            )}
        </div>
      </div>
    </SheetShell>
  );
};
